import React, { useEffect, useState } from "react";
import { AddressType, RsvpType } from "../types/types";
import { db } from "../services/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

const Attendees: React.FC = () => {
  // ---------------- state ----------------
  const [addresses, setAddresses] = useState<AddressType[]>([]);
  const [rsvps, setRsvps] = useState<RsvpType[]>([]);

  // ---------------- data fetch ----------------
  useEffect(() => {
    const fetchAll = async () => {
      const addrSnap = await getDocs(collection(db, "addresses"));
      setAddresses(addrSnap.docs.map((doc) => doc.data() as AddressType));

      const rsvpSnap = await getDocs(collection(db, "rsvps"));
      setRsvps(rsvpSnap.docs.map((doc) => doc.data() as RsvpType));
    };
    fetchAll();
  }, []);

  // ---------------- helpers ----------------
  const isAttending = (r: RsvpType) => r.individualAttendance?.some(Boolean);

  const isDeclined = (r: RsvpType) =>
    r.individualAttendance?.length &&
    r.individualAttendance.every((val) => !val);

  const attendingRsvps = rsvps.filter(isAttending);
  const declinedRsvps = rsvps.filter(isDeclined);

  // build a key set to find missing RSVPs
  const rsvpKeys = new Set(rsvps.map((r) => r.names.join(", ")));
  const notRsvped = addresses.filter((a) => !rsvpKeys.has(a.names.join(", ")));

  // pretty boolean → Yes/No
  const yn = (b: boolean) => (b ? "Yes" : "No");

  // reusable display block for an RSVP doc
  const renderRsvpBlock = (r: RsvpType, i: number) => {
    // who said yes / no
    const attendingNames = r.names.filter(
      (_, idx) => r.individualAttendance?.[idx]
    );
    const notAttendingNames = r.names.filter(
      (_, idx) => !r.individualAttendance?.[idx]
    );

    return (
      <div
        key={i}
        className="p-3 fw-normal text-start"
        style={{ marginBottom: "2rem" }}
      >
        <strong>Attending:</strong>{" "}
        {attendingNames.length ? attendingNames.join(", ") : "No one"}
        {notAttendingNames.length > 0 && (
          <>
            <br />
            <em className="text-muted">
              {notAttendingNames.join(", ")} declined
            </em>
          </>
        )}
        <br />
        <strong>Rehearsal Dinner:</strong> {r.rehearsalDinner ? "Yes" : "No"}
        <br />
        <strong>Welcome Drinks:</strong> {r.welcomeDrinks ? "Yes" : "No"}
        <br />
        <strong>Wedding:</strong> {r.wedding ? "Yes" : "No"}
        <br />
        <strong>After Party:</strong> {r.afterParty ? "Yes" : "No"}
        <br />
        <strong>Food allergies:</strong> {r.foodAllergies || "(none)"}
        <br />
        <strong>Song request:</strong> {r.songRequest || "(none)"}
        <br />
        <strong>Extra note:</strong> {r.extraneous || "(none)"}
      </div>
    );
  };


  // ---------------- render ----------------
  return (
    <div className="text-content">
      <h1 className="mb-1">Attendees</h1>

      {/* ----- YES ----- */}
      <div className="simple-card mt-5">
        <h2>Folks who have RSVP’d “Yes”:</h2>
        {attendingRsvps.length ? (
          attendingRsvps.map(renderRsvpBlock)
        ) : (
          <p>No “Yes” RSVPs yet.</p>
        )}
      </div>

      {/* ----- DECLINED ----- */}
      <div className="simple-card mt-5">
        <h2>Folks who declined:</h2>
        {declinedRsvps.length ? (
          declinedRsvps.map((r, i) => (
            <div key={i} className="p-3 fw-normal text-start">
              {r.names.join(", ")} — “Sorry, can’t make it”
            </div>
          ))
        ) : (
          <p>Nobody has declined 🎉</p>
        )}
      </div>

      {/* ----- NO RSVP ----- */}
      <div className="simple-card mt-5">
        <h2>Folks who haven’t RSVP’d yet:</h2>
        {notRsvped.length ? (
          notRsvped.map((a, i) => (
            <div key={i} className="p-3 fw-normal text-start">
              {a.names.join(", ")}
            </div>
          ))
        ) : (
          <p>Everyone has RSVP’d!</p>
        )}
      </div>
    </div>
  );
};

export default Attendees;
